import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { Loader2, Eye, Settings, Clock, LogOut, Download, Share2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { ComplianceDecorationExtension, complianceDecoPluginKey, COMPLIANCE_ACTIVE_META, type ComplianceStatus } from './ComplianceDecorationPlugin'
import { CitationNode } from './CitationNode'
import { FooterParagraph } from './FooterParagraph'
import { SourceRegistryExtension, sourceRegistryPluginKey, HIDDEN_SOURCES_META } from './sourceRegistryPlugin'
import { UniqueId } from './UniqueIdExtension'
import { AuditProtectionExtension, auditProtectionPluginKey } from './AuditProtectionExtension'
import { EditorBubbleMenu } from './EditorBubbleMenu'
import { EditorBottomBar } from './EditorBottomBar'
import { ShareDialog } from './ShareDialog'
import { apiFetch } from '@/lib/apiFetch'
import { citationLabel } from '@/lib/citationLabel'
import { renderMarkdownInline } from '@/lib/renderMarkdownInline'
import { useOrgInfo } from '@/hooks/useOrgInfo'
import { useDraftPresence } from '@/hooks/useDraftPresence'
import type { ChatResponse, AtomicBreakdownEntry, JSONContent } from '@/types/api'
import type { HiddenByClaimUuid } from '@/shared/reviewedDoc'
import type { CitationSnapshot } from '@/types/citation'

interface Abbreviation {
  abbr: string
  full: string
  user_edited?: boolean
}

// ── Presence banner (team mode only) ────────────────────────────────────────
//
// Renders "Someone else has this open" above the editor. Resolves the
// user_id → display name via /api/org/members, which the user can already
// see in the invite modal — no new endpoint, no new data leak.
//
// Cached in a module-level Map so the banner appears immediately on
// subsequent draft switches without re-fetching the directory.

interface OrgMemberLite {
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
}

const memberNameCache = new Map<string, string>()
let memberFetchPromise: Promise<void> | null = null

function memberName(m: OrgMemberLite): string {
  const first = (m.first_name ?? '').trim()
  const last = (m.last_name ?? '').trim()
  if (first || last) return `${first} ${last}`.trim()
  return m.email.split('@')[0]
}

async function ensureMemberCache(): Promise<void> {
  if (memberNameCache.size > 0) return
  if (memberFetchPromise) return memberFetchPromise
  memberFetchPromise = (async () => {
    try {
      const res = await apiFetch('/api/org/members')
      if (!res.ok) return
      const members = (await res.json()) as OrgMemberLite[]
      for (const m of members) memberNameCache.set(m.user_id, memberName(m))
    } catch {
      // Banner will fall back to "Someone".
    } finally {
      memberFetchPromise = null
    }
  })()
  return memberFetchPromise
}

function formatAge(ms: number): string {
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes === 1) return '1 min ago'
  return `${minutes} min ago`
}

function PresenceBanner({ editor }: { editor: { user_id: string; age_ms: number } }) {
  const [name, setName] = useState<string | null>(memberNameCache.get(editor.user_id) ?? null)

  useEffect(() => {
    if (name) return
    let cancelled = false
    ensureMemberCache().then(() => {
      if (cancelled) return
      const resolved = memberNameCache.get(editor.user_id)
      if (resolved) setName(resolved)
    })
    return () => { cancelled = true }
  }, [editor.user_id, name])

  const displayName = name ?? 'Someone'
  return (
    <div className="px-4 py-1.5 text-xs text-amber-900 bg-amber-50 border-b border-amber-200 shrink-0 flex items-center gap-2">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
      <span>
        <strong className="font-medium">{displayName}</strong>
        {' '}has this draft open ({formatAge(editor.age_ms)}). Your saves may
        conflict — we'll warn you if they do.
      </span>
    </div>
  )
}

// --- Simple djb2 hash for staleness detection ---
function djb2(str: string): number {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0
  }
  return hash >>> 0
}

// --- Source focus decoration plugin ---
const focusDecoPluginKey = new PluginKey('sourceFocusDecoration')

function createSourceFocusExtension() {
  return Extension.create({
    name: 'sourceFocusDecoration',

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: focusDecoPluginKey,
          state: {
            init() {
              return { sourceId: null as string | null }
            },
            apply(tr, prev) {
              const meta = tr.getMeta(focusDecoPluginKey)
              if (meta !== undefined) return { sourceId: meta }
              return prev
            },
          },
          props: {
            decorations(state) {
              const { sourceId } = focusDecoPluginKey.getState(state)
              if (!sourceId) return DecorationSet.empty

              // TODO: wire source focus glow to real claim data instead of removed mock data
              const decorations: Decoration[] = []

              return DecorationSet.create(state.doc, decorations)
            },
          },
        }),
      ]
    },
  })
}

function extractTextFromJSON(node: JSONContent): string {
  const parts: string[] = []
  if (typeof (node as Record<string, unknown>).text === 'string')
    parts.push((node as Record<string, unknown>).text as string)
  if (Array.isArray((node as Record<string, unknown>).content))
    for (const child of (node as Record<string, unknown>).content as JSONContent[])
      parts.push(extractTextFromJSON(child))
  return parts.join('')
}

function extractFirstLineFromJSON(node: JSONContent): string {
  if (node.type === 'doc' && Array.isArray(node.content) && node.content.length > 0) {
    return extractTextFromJSON(node.content[0]).trim()
  }
  return extractTextFromJSON(node).trim()
}

/** Pull the filename out of a Content-Disposition header value. Tolerant
 *  of both quoted (`filename="x.zip"`) and unquoted (`filename=x.zip`)
 *  forms and of `filename*=UTF-8''…` extended values. Returns null if
 *  the header is missing or unparseable. */
function parseDownloadFilename(header: string | null): string | null {
  if (!header) return null
  const ext = header.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i)
  if (ext) {
    try { return decodeURIComponent(ext[1].trim()) } catch { /* fall through */ }
  }
  const std = header.match(/filename\s*=\s*"?([^";]+)"?/i)
  return std ? std[1].trim() : null
}

interface WorkbenchProps {
  onClaimSelect: (claimId: string | null) => void
  onReviewClick: () => void
  isAuditing?: boolean
  onAuditComplete?: () => void
  highlightsVisible?: boolean
  onOpenSettings?: () => void
  focusedSourceId?: string | null
  onEditorReady?: (helpers: {
    getText: () => string
    getJSON: () => JSONContent
    setContent: (content: JSONContent) => void
    setEditable: (editable: boolean) => void
    /** Find first occurrence of `search` in text nodes and replace with `replace`. Returns true if found. */
    replaceText: (search: string, replace: string) => boolean
    /** Remove the citation node whose `claimId` attribute matches the given uuid.
     *  Used by the "Remove citation" flow (D4). Bypasses AuditProtection.
     *  Goes through addToHistory so in-session ⌘Z restores it. Returns the
     *  captured node attrs + position so callers can store them for the
     *  session-scoped restore stack (D11). Returns null if no node matched. */
    removeCitationByClaimUuid: (claimUuid: string) => CitationSnapshot | null
    /** Re-insert a citation node at the given doc position. Used by the
     *  session-scoped restore stack (D11). Bypasses AuditProtection. */
    insertCitationAt: (
      pos: number,
      attrs: { claimId: string; attachmentId?: string | null; sourceUuids: string; indices?: string; claimIndices?: string | null; sentenceHash?: string | null },
    ) => boolean
  }) => void
  /** Real API audit result — triggers citation visualization in the editor */
  apiAuditResult?: (ChatResponse & { query: string }) | null
  /** Whether the audit result came from an incremental audit (skip client-side citation injection) */
  isIncrementalResult?: boolean
  /** Called when user clicks an API-flow citation `[N]` in the editor */
  onCitationClick?: (claimIndices: number[]) => void
  /** Live progress message from the backend during polling */
  progressMessage?: string | null
  /** Email of the logged-in user, shown in the toolbar */
  userEmail?: string
  /** Called when the user clicks the logout button */
  onLogout?: () => void
  /** Saved draft content to load into the editor */
  initialContent?: JSONContent | null
  /** Saved draft title */
  initialTitle?: string
  /** Called when editor content changes (for auto-save) */
  onContentChange?: (json: JSONContent) => void
  /** Called when title changes (for auto-save) */
  onTitleChange?: (title: string) => void
  /** Current save status */
  saveStatus?: 'idle' | 'saving' | 'saved'
  /** UUID → LibraryItem lookup for resolving human-readable source citations */
  sourceLookup?: Map<string, import('@/data/mock').LibraryItem>
  /** Called when active breakdown indices change (for review panel pruning) */
  onActiveIndicesChange?: (indices: Set<number>) => void
  /** Index of the claim currently in surgery mode (for text highlight sync) */
  activeSurgeryClaimIndex?: number | null
  /** Effective claim statuses from source removal / manual overrides — merged into decoration plugin */
  claimStatusOverrides?: Map<string, string>
  /** Called when editor content diverges from / returns to the audit baseline */
  onStaleChange?: (isStale: boolean) => void
  /** Per-claim hidden-source map (keyed by claim_uuid). Drives reactive
   *  citation-label/bibliography filter (PR4) and reviewed-doc export (PR2). */
  hiddenSourcesByClaimUuid?: HiddenByClaimUuid
  /** Active draft id — required for the Tier-1 Export button (Phase 7). */
  draftId?: string | null
  /** Open the right panel into the abbreviations editor (footer "Edit" pill). */
  onEditAbbreviations?: () => void
  /** Current user's WorkOS id. Threaded through so the presence hook can
   *  filter ourselves out of "Someone else is editing" banners. Optional
   *  for the same reason ``userEmail`` is — pre-auth states still render
   *  the editor in some flows. */
  currentUserId?: string | null
}

export function Workbench({ onClaimSelect, onReviewClick, isAuditing = false, onAuditComplete, highlightsVisible = true, onOpenSettings, focusedSourceId = null, onEditorReady, apiAuditResult, isIncrementalResult = false, onCitationClick, progressMessage, userEmail, onLogout, initialContent, initialTitle, onContentChange, onTitleChange, saveStatus, sourceLookup, onActiveIndicesChange, activeSurgeryClaimIndex = null, claimStatusOverrides, onStaleChange, hiddenSourcesByClaimUuid, draftId = null, onEditAbbreviations, currentUserId = null }: WorkbenchProps) {
  // Team-mode signal: only show presence UI when ≥2 active members. Solo
  // users (and the loading state, conservatively) see no banner.
  const { org } = useOrgInfo()
  const isTeamMode = !!org?.is_team_mode
  const otherEditor = useDraftPresence(draftId, currentUserId, isTeamMode)
  const [title, setTitle] = useState(initialTitle ?? 'Untitled')
  const [bibliography, setBibliography] = useState<{ id: number; source_doc: string }[]>([])
  const [abbreviations, setAbbreviations] = useState<Abbreviation[]>([])
  const [isExporting, setIsExporting] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const onClaimSelectRef = useRef(onClaimSelect)
  const onAuditCompleteRef = useRef(onAuditComplete)
  const onCitationClickRef = useRef(onCitationClick)
  const onContentChangeRef = useRef(onContentChange)
  const onActiveIndicesChangeRef = useRef(onActiveIndicesChange)
  const claimStatusOverridesRef = useRef(claimStatusOverrides)
  const onStaleChangeRef = useRef(onStaleChange)
  const hiddenSourcesByClaimUuidRef = useRef(hiddenSourcesByClaimUuid)
  const auditBaselineHashRef = useRef<number | null>(null)
  const staleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onTitleChangeRef = useRef(onTitleChange)
  const titleRef = useRef(title)
  const isManualTitleRef = useRef(false)
  const bibliographyRef = useRef(bibliography)
  const apiAuditResultRef = useRef(apiAuditResult)
  // Keep latest-value refs in sync after each render so callbacks fired
  // from event handlers / timers / async work read up-to-date values.
  useEffect(() => {
    onClaimSelectRef.current = onClaimSelect
    onAuditCompleteRef.current = onAuditComplete
    onCitationClickRef.current = onCitationClick
    onContentChangeRef.current = onContentChange
    onActiveIndicesChangeRef.current = onActiveIndicesChange
    claimStatusOverridesRef.current = claimStatusOverrides
    onStaleChangeRef.current = onStaleChange
    hiddenSourcesByClaimUuidRef.current = hiddenSourcesByClaimUuid
    onTitleChangeRef.current = onTitleChange
    titleRef.current = title
    bibliographyRef.current = bibliography
    apiAuditResultRef.current = apiAuditResult
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync title state when parent swaps documents
    setTitle(initialTitle ?? 'Untitled')
    isManualTitleRef.current = false
  }, [initialTitle])

  const sourceFocusExtension = useMemo(() => createSourceFocusExtension(), [])

  const editor = useEditor({
    extensions: [
      StarterKit,
      UniqueId,
      Placeholder.configure({
        placeholder: 'Start writing your document...',
      }),
      CitationNode,
      FooterParagraph,
      SourceRegistryExtension,
      ComplianceDecorationExtension,
      AuditProtectionExtension,
      sourceFocusExtension,
    ],
    content: initialContent || '',
    onUpdate: ({ editor }) => {
      // eslint-disable-next-line no-restricted-properties -- canonical AI-output auto-save: review_state is persisted separately, so the saved doc is intentionally unfiltered.
      const json = editor.getJSON()
      onContentChangeRef.current?.(json)

      // Auto-generate title from first line of content (unless manually edited)
      if (!isManualTitleRef.current) {
        const firstLine = extractFirstLineFromJSON(json)
        const words = firstLine.split(/\s+/).filter(Boolean)
        if (words.length >= 3) {
          const generated = words.slice(0, 6).join(' ')
          const newTitle = generated.length > 50 ? generated.slice(0, 47) + '...' : generated
          if (newTitle !== titleRef.current) {
            setTitle(newTitle)
            onTitleChangeRef.current?.(newTitle)
          }
        }
      }

    },
    editorProps: {
      attributes: {
        class: 'prose prose-slate max-w-none focus:outline-none',
      },
      handleClick: (_view, _pos, event) => {
        const target = event.target as HTMLElement

        // Handle citation click (sup[data-citation] covers both flows)
        const citationEl = target.closest('sup[data-citation]')
        if (citationEl) {
          // API-flow citation: has data-claim-indices → highlight sidebar cards
          const claimIndicesRaw = citationEl.getAttribute('data-claim-indices')
          if (claimIndicesRaw !== null) {
            try {
              const parsed: number[] = JSON.parse(claimIndicesRaw)
              onCitationClickRef.current?.(parsed)
            } catch { /* ignore malformed */ }
            return true
          }
          // Legacy mock citation: has data-claim-id → open EvidenceLocker
          const claimId = citationEl.getAttribute('data-claim-id')
          if (claimId) onClaimSelectRef.current(claimId)
          return true
        }

        // Handle compliance mark click
        const complianceEl = target.closest('[data-compliance-id]')
        if (complianceEl) {
          const id = complianceEl.getAttribute('data-compliance-id')
          if (id) onClaimSelectRef.current(id)
        }
        return false
      },
    },
  })

  // Dispatch source focus decoration changes to the ProseMirror plugin
  useEffect(() => {
    if (!editor) return
    const { tr } = editor.state
    tr.setMeta(focusDecoPluginKey, focusedSourceId)
    editor.view.dispatch(tr)
  }, [focusedSourceId, editor])

  // Surgery mode highlight sync — dispatch active compliance ID to the decoration plugin.
  // The plugin includes `compliance-active` in the decoration class string, so the
  // highlight survives ProseMirror re-renders (no DOM manipulation needed).
  useEffect(() => {
    if (!editor) return

    // Resolve the compliance ID by walking the ProseMirror doc (not the DOM)
    let targetComplianceId: string | null = null
    if (activeSurgeryClaimIndex != null) {
      editor.state.doc.descendants((node) => {
        if (targetComplianceId) return false
        if (node.type.name === 'citation' && node.attrs.claimIndices) {
          try {
            const indices: number[] = JSON.parse(node.attrs.claimIndices)
            if (indices.includes(activeSurgeryClaimIndex)) {
              targetComplianceId = node.attrs.claimId
            }
          } catch { /* ignore */ }
        }
      })
    }

    // Dispatch to the decoration plugin
    const { tr } = editor.state
    tr.setMeta(COMPLIANCE_ACTIVE_META, targetComplianceId)
    editor.view.dispatch(tr)

    // Scroll into view after PM renders the updated decorations
    if (targetComplianceId) {
      requestAnimationFrame(() => {
        const activeEl = editor.view.dom.querySelector('.compliance-active')
        activeEl?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    }
  }, [activeSurgeryClaimIndex, editor])

  // Reactive bibliography: derived from SourceRegistryExtension on every doc change
  useEffect(() => {
    if (!editor) return

    const syncFromDoc = () => {
      // Sync bibliography from SourceRegistry plugin state
      const registry = sourceRegistryPluginKey.getState(editor.state)
      if (!registry || registry.sourceMap.size === 0) {
        if (bibliographyRef.current.length > 0) setBibliography([])
      } else {
        const newBib = Array.from(registry.sourceMap.entries())
          .map(([source_doc, id]) => ({ id, source_doc }))
          .sort((a, b) => a.id - b.id)
        const cur = bibliographyRef.current
        if (
          newBib.length !== cur.length ||
          newBib[0]?.source_doc !== cur[0]?.source_doc ||
          newBib[newBib.length - 1]?.source_doc !== cur[cur.length - 1]?.source_doc
        ) {
          setBibliography(newBib)
        }
      }

      // Sync active breakdown indices from CitationNodes
      const indices = new Set<number>()
      editor.state.doc.descendants((node) => {
        if (node.type.name === 'citation' && node.attrs.claimIndices) {
          try {
            const parsed: number[] = JSON.parse(node.attrs.claimIndices)
            for (const idx of parsed) indices.add(idx)
          } catch { /* ignore */ }
        }
      })
      onActiveIndicesChangeRef.current?.(indices)

      // Sync compliance decorations from CitationNodes + audit results
      const auditResult = apiAuditResultRef.current
      if (auditResult && auditResult.atomic_breakdown.length > 0) {
        const statusMap = buildClaimStatusMap(editor, auditResult.atomic_breakdown, claimStatusOverridesRef.current)
        if (statusMap.size > 0) {
          const { tr } = editor.state
          tr.setMeta(complianceDecoPluginKey, statusMap)
          editor.view.dispatch(tr)
        }
      }

      // Debounced staleness check — compare current text hash to baseline
      if (auditBaselineHashRef.current !== null) {
        if (staleTimerRef.current) clearTimeout(staleTimerRef.current)
        staleTimerRef.current = setTimeout(() => {
          if (!editor) return
          const currentHash = djb2(editor.getText())
          onStaleChangeRef.current?.(currentHash !== auditBaselineHashRef.current)
        }, 500)
      }
    }

    editor.on('update', syncFromDoc)
    syncFromDoc() // initial computation for loaded content
    return () => { editor.off('update', syncFromDoc) }
  }, [editor])

  // Visualize API audit citations in the editor when results arrive
  useEffect(() => {
    if (!editor || !apiAuditResult) return
    if (isIncrementalResult) {
      // Hydrated AST already has citation nodes — bibliography is derived reactively
    } else {
      visualizeApiCitations(editor, apiAuditResult.atomic_breakdown)
    }
    // Use abbreviations from the audit response (already extracted by the pipeline)
    if (apiAuditResult.abbreviations?.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync local abbreviations from server-derived audit payload
      setAbbreviations(apiAuditResult.abbreviations)
    }

    // Also dispatch statusMap here (in addition to syncFromDoc) to handle
    // the case where apiAuditResult updates AFTER editor content is already loaded.
    // syncFromDoc handles the reverse: editor content changes after apiAuditResult is set.
    // Belt-and-suspenders — both are idempotent, whichever has both pieces of data wins.
    // Read overrides via ref so this effect doesn't re-run on override changes —
    // the dedicated effect below handles override updates.
    const statusMap = buildClaimStatusMap(editor, apiAuditResult.atomic_breakdown, claimStatusOverridesRef.current)
    if (statusMap.size > 0) {
      const { tr } = editor.state
      tr.setMeta(complianceDecoPluginKey, statusMap)
      editor.view.dispatch(tr)
    }
    // Capture content baseline hash for staleness detection
    auditBaselineHashRef.current = djb2(editor.getText())
  }, [apiAuditResult, editor, isIncrementalResult])

  // Re-dispatch decoration statusMap when claim status overrides change (source removal / manual overrides)
  useEffect(() => {
    if (!editor || !apiAuditResult || !claimStatusOverrides || claimStatusOverrides.size === 0) return
    const statusMap = buildClaimStatusMap(editor, apiAuditResult.atomic_breakdown, claimStatusOverrides)
    if (statusMap.size > 0) {
      const { tr } = editor.state
      tr.setMeta(complianceDecoPluginKey, statusMap)
      editor.view.dispatch(tr)
    }
  }, [claimStatusOverrides, editor, apiAuditResult])

  // Push hidden-source map into the source registry plugin whenever it changes.
  // The plugin filters citation labels reactively. The bibliography is React
  // state outside the editor and the syncFromDoc listener only fires on
  // doc-changed transactions, so we explicitly sync it here from the now-
  // filtered registry. review_state stays the canonical record; the doc
  // remains unmodified.
  useEffect(() => {
    if (!editor) return
    const map = hiddenSourcesByClaimUuid ?? new Map<string, Set<string>>()
    const { tr } = editor.state
    tr.setMeta(HIDDEN_SOURCES_META, map)
    tr.setMeta('addToHistory', false)
    editor.view.dispatch(tr)

    const registry = sourceRegistryPluginKey.getState(editor.state)
    if (!registry) return
    const newBib = Array.from(registry.sourceMap.entries())
      .map(([source_doc, id]) => ({ id, source_doc }))
      .sort((a, b) => a.id - b.id)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- bibliography derived from external ProseMirror plugin registry
    setBibliography(newBib)
  }, [editor, hiddenSourcesByClaimUuid])

  // Expose editor helpers to parent once the editor is ready
  const onEditorReadyRef = useRef(onEditorReady)
  useEffect(() => { onEditorReadyRef.current = onEditorReady })
  useEffect(() => {
    if (editor) {
      onEditorReadyRef.current?.({
        getText: () => editor.getText(),
        // eslint-disable-next-line no-restricted-properties -- raw AI-output accessor exposed to parents for auto-save and debug. The reviewed view (hidden-source filter, bibliography renumbering) is enforced server-side in backend/core/export/review_filter.py.
        getJSON: () => editor.getJSON() as JSONContent,
        setContent: (content) => {
          const newDoc = editor.schema.nodeFromJSON(content)
          const tr = editor.state.tr
          tr.replaceWith(0, editor.state.doc.content.size, newDoc.content)
          tr.setMeta(auditProtectionPluginKey, { bypass: true })
          tr.setMeta('addToHistory', false)
          editor.view.dispatch(tr)
        },
        setEditable: (editable) => editor.setEditable(editable),
        replaceText: (search, replace) => {
          const { doc } = editor.state
          const tr = editor.state.tr
          let found = false
          doc.descendants((node, pos) => {
            if (found || !node.isText) return
            const idx = node.text?.indexOf(search) ?? -1
            if (idx === -1) return
            const start = pos + idx
            const end = start + search.length
            tr.insertText(replace, start, end)
            found = true
            return false
          })
          if (found) editor.view.dispatch(tr)
          return found
        },
        removeCitationByClaimUuid: (claimUuid: string) => {
          const { doc, tr } = editor.state
          let captured: CitationSnapshot | null = null
          let foundFrom = -1
          let foundTo = -1
          doc.descendants((node, pos) => {
            if (captured) return false
            if (node.type.name === 'citation' && node.attrs?.claimId === claimUuid) {
              const attrs = node.attrs
              captured = {
                claimId: attrs.claimId,
                attachmentId: attrs.attachmentId ?? null,
                sourceUuids: attrs.sourceUuids ?? '[]',
                indices: attrs.indices,
                claimIndices: attrs.claimIndices ?? null,
                sentenceHash: attrs.sentenceHash ?? null,
                originalPos: pos,
                removedAt: new Date(),
              }
              foundFrom = pos
              foundTo = pos + node.nodeSize
              return false
            }
          })
          if (!captured) return null
          tr.delete(foundFrom, foundTo)
          tr.setMeta(auditProtectionPluginKey, { bypass: true })
          editor.view.dispatch(tr)
          return captured
        },
        insertCitationAt: (pos, attrs) => {
          const { doc, tr } = editor.state
          if (pos < 0 || pos > doc.content.size) return false
          const node = editor.schema.nodes.citation?.create(attrs)
          if (!node) return false
          tr.insert(pos, node)
          tr.setMeta(auditProtectionPluginKey, { bypass: true })
          editor.view.dispatch(tr)
          return true
        },
      })
    }
  }, [editor])

  // Deduplicate adjacent citation nodes (cleanup for previously corrupted drafts)
  useEffect(() => {
    if (!editor) return
    const { tr } = editor.state
    const toRemove: { from: number; to: number }[] = []

    editor.state.doc.descendants((node, pos, _parent, _index) => {
      if (node.type.name !== 'citation') return
      const nextPos = pos + node.nodeSize
      const nextNode = tr.doc.nodeAt(nextPos)
      if (
        nextNode?.type.name === 'citation' &&
        nextNode.attrs.indices === node.attrs.indices
      ) {
        toRemove.push({ from: nextPos, to: nextPos + nextNode.nodeSize })
      }
    })

    // Delete in reverse order to preserve positions
    for (let i = toRemove.length - 1; i >= 0; i--) {
      tr.delete(toRemove[i].from, toRemove[i].to)
    }
    if (toRemove.length > 0) {
      tr.setMeta(auditProtectionPluginKey, { bypass: true })
      tr.setMeta('addToHistory', false)
      editor.view.dispatch(tr)
    }
  }, [editor])

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      {/* Team-mode presence banner. Only renders when org has ≥2 members
          AND another user has heartbeated this draft in the last 5 min.
          Intentionally soft: a heads-up, not a lock. Solo users never
          see this code path (isTeamMode gates the hook). */}
      {isTeamMode && otherEditor && (
        <PresenceBanner editor={otherEditor} />
      )}

      {/* Header Row - h-12 to match RightPanel header */}
      <div className="h-12 flex items-center justify-between border-b border-border px-4 shrink-0">
        {/* Left: Editable Title */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <input
            type="text"
            value={title}
            onChange={(e) => {
              isManualTitleRef.current = true
              setTitle(e.target.value)
              onTitleChangeRef.current?.(e.target.value)
            }}
            className="text-sm font-semibold text-foreground bg-transparent border-none outline-none w-full max-w-md truncate placeholder:text-muted-foreground"
            placeholder="Untitled document"
          />
          {saveStatus === 'saving' && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">Saving...</span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">Saved</span>
          )}
        </div>

        {/* Right: Action Buttons */}
        <div className="flex items-center gap-1 shrink-0">
          <Button
            size="sm"
            onClick={onReviewClick}
            className="bg-pharma-primary hover:bg-pharma-primary/90 text-white text-[13px] gap-2"
          >
            <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
            Review
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-[13px] gap-2 text-muted-foreground hover:text-foreground"
            disabled={!draftId || isExporting}
            onClick={async () => {
              if (!draftId) return
              setIsExporting(true)
              try {
                const res = await apiFetch(`/api/drafts/${draftId}/export`, { method: 'POST' })
                if (!res.ok) {
                  let detail = res.statusText
                  try {
                    const body = await res.json()
                    detail = body?.detail ?? detail
                  } catch { /* response wasn't JSON; keep statusText */ }
                  toast.error(`Export failed: ${detail}`)
                  return
                }
                const blob = await res.blob()
                const filename = parseDownloadFilename(res.headers.get('Content-Disposition'))
                  ?? `${title || 'export'}_export.zip`
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = filename
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(url)
                toast.success(`Exported ${filename}`)
              } catch (err) {
                toast.error(`Export failed: ${err instanceof Error ? err.message : String(err)}`)
              } finally {
                setIsExporting(false)
              }
            }}
          >
            {isExporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> : <Download className="h-3.5 w-3.5" strokeWidth={1.5} />}
            Export
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[13px] gap-2 text-muted-foreground hover:text-foreground"
                  disabled={!draftId}
                  onClick={() => setShareOpen(true)}
                >
                  <Share2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Share
                </Button>
              </span>
            </TooltipTrigger>
            {!draftId && <TooltipContent>Save the draft first</TooltipContent>}
          </Tooltip>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpenSettings?.()}>
            <Settings className="h-4 w-4" strokeWidth={1.5} />
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground pointer-events-none"
                  disabled
                  aria-label="Version history — coming soon"
                >
                  <Clock className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Version history — coming soon</TooltipContent>
          </Tooltip>
          {onLogout && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground"
              onClick={onLogout}
              title={userEmail ? `Sign out (${userEmail})` : 'Sign out'}
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          )}
        </div>
      </div>

      {/* Editor Area */}
      <div className="relative flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto bg-muted/30 p-8 pb-20">
          <div
            className={`relative mx-auto max-w-[800px] bg-white shadow-sm rounded-lg p-12 min-h-[600px] ${!highlightsVisible ? 'hide-compliance-decos' : ''}`}
            onClick={(e) => {
              // Focus editor when clicking empty card space below text
              if (e.target === e.currentTarget && editor) {
                editor.commands.focus('end')
              }
            }}
          >
            {isAuditing && (
              <div className="absolute inset-0 bg-white/60 rounded-lg z-10 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3 text-pharma-primary">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="text-sm font-medium">
                    {progressMessage ?? 'Starting audit...'}
                  </span>
                </div>
              </div>
            )}
            {editor && <EditorBubbleMenu editor={editor} />}
            <EditorContent editor={editor} />
            {(abbreviations.length > 0 || bibliography.length > 0) && (
              <div className="mt-4 border-t pt-3 space-y-1">
                {abbreviations.length > 0 && (
                  <p className="text-xs text-gray-400 leading-relaxed">
                    {abbreviations.map((a) => `${a.abbr}: ${a.full}`).join('; ')}
                    {onEditAbbreviations && (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={onEditAbbreviations}
                          className="text-[11px] text-gray-500 hover:text-gray-700 underline underline-offset-2 ml-1"
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </p>
                )}
                {bibliography.length > 0 && (
                  <p className="text-xs text-gray-400 leading-relaxed">
                    {bibliography.map((ref, i) => (
                      <React.Fragment key={ref.id}>
                        {i > 0 && '  '}
                        {ref.id}. {(() => {
                          const src = sourceLookup?.get(ref.source_doc)
                          return renderMarkdownInline(citationLabel(src, ref.source_doc))
                        })()}
                      </React.Fragment>
                    ))}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Toolbar */}
        {editor && <EditorBottomBar editor={editor} />}
      </div>
      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        draftId={draftId}
        draftTitle={title}
      />
    </div>
  )
}

// --- Helper: Build bibliography from atomic breakdown (without inserting citation nodes) ---
// Used when the hydrated AST already has citation nodes (incremental / draft-based audits).
/**
 * Build a Map<claimId, ComplianceStatus> by walking CitationNodes in the doc
 * and matching their claimIndices to atomic_breakdown entries.
 */
function buildClaimStatusMap(
  editor: ReturnType<typeof useEditor>,
  breakdown: AtomicBreakdownEntry[],
  overrides?: Map<string, string>,
): Map<string, ComplianceStatus> {
  const statusMap = new Map<string, ComplianceStatus>()
  if (!editor || breakdown.length === 0) return statusMap

  // Build index→status lookup from breakdown
  const indexToStatus = new Map<number, ComplianceStatus>()
  for (let i = 0; i < breakdown.length; i++) {
    const entry = breakdown[i]
    if (entry.status && entry.status !== 'error') {
      indexToStatus.set(i, entry.status as ComplianceStatus)
    }
  }

  // Walk CitationNodes, match via claimIndices.
  // Also build a breakdown-index → node-claimId map for override translation.
  const indexToNodeClaimId = new Map<number, string>()
  editor.state.doc.descendants((node) => {
    if (node.type.name !== 'citation' || !node.attrs.claimId) return
    const rawIndices = node.attrs.claimIndices
    if (!rawIndices) return
    try {
      const indices: number[] = JSON.parse(rawIndices)
      for (const idx of indices) {
        indexToNodeClaimId.set(idx, node.attrs.claimId)
        const status = indexToStatus.get(idx)
        if (status) {
          statusMap.set(node.attrs.claimId, status)
          break // first match determines the status for this claimId
        }
      }
    } catch { /* ignore malformed */ }
  })

  // Merge overrides from source-removal recalculation + manual overrides.
  // Override keys are breakdown indices ("0", "1"), statusMap keys are node claimIds ("api-ref-0").
  // Use indexToNodeClaimId to translate.
  if (overrides) {
    for (const [overrideKey, status] of overrides) {
      if (status === 'error') continue
      const idx = parseInt(overrideKey, 10)
      const nodeClaimId = Number.isNaN(idx) ? overrideKey : indexToNodeClaimId.get(idx)
      if (nodeClaimId && statusMap.has(nodeClaimId)) {
        statusMap.set(nodeClaimId, status as ComplianceStatus)
      }
    }
  }

  return statusMap
}

// --- Helper: Insert API-flow citation superscripts (bibliography style) ---
// Uses late-binding: passes sourceUuids to citation nodes, display numbers
// are computed by the Source Registry ProseMirror plugin at render time.
// Bibliography is derived reactively from SourceRegistryExtension — not set here.
function visualizeApiCitations(
  editor: ReturnType<typeof useEditor>,
  breakdown: AtomicBreakdownEntry[],
) {
  if (!editor) return

  // 1. Remove all existing citation nodes before re-inserting from API results
  {
    const { tr } = editor.state
    const toRemove: { from: number; to: number }[] = []
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'citation') {
        toRemove.push({ from: pos, to: pos + node.nodeSize })
      }
    })
    for (let i = toRemove.length - 1; i >= 0; i--) {
      tr.delete(toRemove[i].from, toRemove[i].to)
    }
    if (toRemove.length > 0) {
      tr.setMeta(auditProtectionPluginKey, { bypass: true })
      tr.setMeta('addToHistory', false)
      editor.view.dispatch(tr)
    }
  }

  // 2. Collect all unique source UUIDs in first-appearance order and build
  //    claimIndex → sourceUuids mapping
  const sourceToRefId = new Map<string, number>()
  const refIdToClaimIndices = new Map<number, number[]>()
  let nextRefId = 1
  const allSourceUuids: string[] = []

  for (let i = 0; i < breakdown.length; i++) {
    const entry = breakdown[i]
    const seenInEntry = new Set<string>()
    for (const ev of entry.evidences ?? []) {
      const sourceDoc = ev.source_doc
      if (!sourceDoc || seenInEntry.has(sourceDoc)) continue
      seenInEntry.add(sourceDoc)
      if (!sourceToRefId.has(sourceDoc)) {
        sourceToRefId.set(sourceDoc, nextRefId++)
        allSourceUuids.push(sourceDoc)
      }
      const refId = sourceToRefId.get(sourceDoc)!
      const existing = refIdToClaimIndices.get(refId) ?? []
      existing.push(i)
      refIdToClaimIndices.set(refId, existing)
    }
  }

  // Bibliography is derived reactively — no imperative setBibliography here

  if (sourceToRefId.size === 0) return

  // 3. Find the end position of the first substantial paragraph
  const getFirstParaEnd = (): number | null => {
    let pos: number | null = null
    editor.state.doc.descendants((node, nodePos) => {
      if (pos !== null) return false
      if (node.type.name === 'paragraph' && node.textContent.length > 10) {
        pos = nodePos + node.nodeSize - 1
      }
    })
    return pos
  }

  // 4. Insert one merged citation node with all sourceUuids
  const allRefIds = [...sourceToRefId.values()].sort((a, b) => a - b)
  const allClaimIndices = allRefIds.flatMap(refId => refIdToClaimIndices.get(refId) ?? [])
  const insertPos = getFirstParaEnd()
  if (insertPos === null) return
  editor
    .chain()
    .setTextSelection(insertPos)
    .command(({ tr }) => {
      tr.setMeta(auditProtectionPluginKey, { bypass: true })
      tr.setMeta('addToHistory', false)
      return true
    })
    .insertCitation({
      claimId: `api-ref-${allRefIds.join('-')}`,
      sourceUuids: JSON.stringify(allSourceUuids),
      indices: JSON.stringify(allRefIds),  // backward compat
      claimIndices: JSON.stringify(allClaimIndices),
    })
    .run()
}

